import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface WorkType {
  id: string;
  name: string;
  rate?: number;
}

const WorkTypesManagement: React.FC = () => {
  const { t } = useTranslation();
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkType, setEditingWorkType] = useState<WorkType | null>(null);
  const [formData, setFormData] = useState({ name: '', rate: '' });

  useEffect(() => {
    loadWorkTypes();
  }, []);

  const loadWorkTypes = async () => {
    try {
      const res = await api.get('/reference/work-types');
      setWorkTypes(res.data);
    } catch (err) {
      console.error('Failed to load work types:', err);
    }
  };

  const handleOpenDialog = (workType?: WorkType) => {
    if (workType) {
      setEditingWorkType(workType);
      setFormData({ name: workType.name, rate: workType.rate?.toString() || '' });
    } else {
      setEditingWorkType(null);
      setFormData({ name: '', rate: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: formData.name,
        rate: formData.rate ? parseFloat(formData.rate) : null,
      };
      if (editingWorkType) {
        await api.put(`/reference/work-types/${editingWorkType.id}`, data);
      } else {
        await api.post('/reference/work-types', data);
      }
      setDialogOpen(false);
      loadWorkTypes();
    } catch (err) {
      console.error('Failed to save work type:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('admin.deleteWorkTypeConfirm'))) return;
    try {
      await api.delete(`/reference/work-types/${id}`);
      loadWorkTypes();
    } catch (err) {
      console.error('Failed to delete work type:', err);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        {t('admin.addWorkType')}
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell>{t('admin.rateUahHr')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workTypes.map((wt) => (
              <TableRow key={wt.id}>
                <TableCell>{wt.name}</TableCell>
                <TableCell>{wt.rate || '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(wt)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(wt.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingWorkType ? t('admin.editWorkType') : t('admin.addWorkType')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('common.name')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('admin.rateUahHr')}
            type="number"
            value={formData.rate}
            onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkTypesManagement;



